import { useState } from 'react';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';

/**
 * Sends the viewer's words about an App Output's App to the Workbook
 * assistant on this page, naming the Output so it builds or changes that one.
 */
export function useWorkbookAppRequest(workbookId: string, outputId: string) {
  const [failed, setFailed] = useState(false);
  const { moduleConfig } = useAgentContainer();
  const { open } = useAgentPanel();
  const { sendMessage, isLoading } = useAgentState();
  const ready =
    moduleConfig?.moduleKey === 'workbooks' &&
    moduleConfig.currentPage === `workbook:${workbookId}`;

  /** Resolves to whether the request went to the assistant. */
  async function send(request: string): Promise<boolean> {
    const text = request.trim();
    if (!ready || isLoading || !text) return false;
    setFailed(false);
    open();
    try {
      const outcome = await sendMessage(
        text,
        'workbooks',
        moduleConfig.currentPage,
        { ...moduleConfig.getModuleContext?.(), appRequest: { outputId } },
        moduleConfig.pageKey,
        moduleConfig.tools,
      );
      // Refused (e.g. the thread is too long): nothing was sent, keep the words.
      if (outcome === 'rejected') setFailed(true);
      return outcome === 'sent';
    } catch {
      setFailed(true);
      return false;
    }
  }

  return { canSend: ready && !isLoading, failed, send };
}
